import type { Series } from "../types";

// Paleta alinhada ao design system (forest / info / ai / coral / primary) — mesmas
// famílias de cor usadas nos gráficos do dashboard (ex.: consumo de créditos).
export const DEFAULT_PALETTE = [
  "#24776F", // forest-500
  "#2E78CA", // info-500
  "#C7B7E8", // ai-500
  "#F07575", // coral-500
  "#F9E10D", // primary-500
];

// ── i18n de rótulos de eixo (meses / dias da semana vêm em inglês do datalake) ──
const MONTHS_EN = [
  "january",
  "february",
  "march",
  "april",
  "may",
  "june",
  "july",
  "august",
  "september",
  "october",
  "november",
  "december",
];
const WEEKDAYS_EN = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
];
/** Função de tradução compatível com o i18next (`t(key, { defaultValue })`). */
type TranslateFn = (key: string, opts?: { defaultValue?: string }) => string;

function translateCanonical(
  t: TranslateFn,
  keyPrefix: string,
  canonical: string,
  abbreviated: boolean,
): string | null {
  const full = t(`${keyPrefix}-${canonical}`, { defaultValue: "" });
  if (!full) return null; // sem chave para o idioma → caller cai no original
  return abbreviated ? full.slice(0, 3) : full;
}

/**
 * Traduz um rótulo de eixo em inglês (mês/dia da semana, completo ou abreviado) para o
 * idioma do cliente, via translations do core (`agent-block-month-*` /
 * `agent-block-weekday-*`). Adicionar um idioma = adicionar as chaves no arquivo dele.
 * Recebe o `t` do i18next; rótulos que não são mês/dia passam intactos.
 */
export function formatAxisLabel(
  label: string | number,
  t: TranslateFn,
): string {
  const raw = String(label ?? "");
  const key = raw.trim().toLowerCase();
  if (!key) return raw;
  const abbreviated = key.length <= 3;

  const monthIdx = MONTHS_EN.findIndex(
    (m) => m === key || m.slice(0, 3) === key,
  );
  if (monthIdx >= 0) {
    return (
      translateCanonical(
        t,
        "agent-block-month",
        MONTHS_EN[monthIdx],
        abbreviated,
      ) ?? raw
    );
  }

  const dayIdx = WEEKDAYS_EN.findIndex(
    (d) => d === key || d.slice(0, 3) === key,
  );
  if (dayIdx >= 0) {
    return (
      translateCanonical(
        t,
        "agent-block-weekday",
        WEEKDAYS_EN[dayIdx],
        abbreviated,
      ) ?? raw
    );
  }

  return raw;
}

export function hashCode(str: string) {
  let h = 0;
  for (let i = 0; i < str.length; i++) {
    h = (h << 5) - h + str.charCodeAt(i);
    h |= 0;
  }
  return Math.abs(h);
}
export function hslByString(key: string, s = 60, l = 55) {
  const hue = hashCode(key) % 360;
  return `hsl(${hue} ${s}% ${l}%)`;
}

export function seriesColor(
  series: Series[],
  palette: string[],
  randomize: boolean,
) {
  const map = new Map<string, string>();
  series.forEach((s, idx) => {
    const key = s.name || `serie_${idx}`;
    const color = randomize ? hslByString(key) : palette[idx % palette.length];
    map.set(s.name, color);
  });
  return map;
}

export function categoryColor(
  x: string[],
  palette: string[],
  randomize: boolean,
) {
  const map = new Map<string, string>();
  x.forEach((label, idx) => {
    const key = String(label);
    const color = randomize ? hslByString(key) : palette[idx % palette.length];
    map.set(key, color);
  });
  return map;
}
